import React from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  ChevronDownIcon,
  BuildingStorefrontIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

const PharmacySelector: React.FC = () => {
  const { pharmacies, selectedPharmacy, selectPharmacy, user } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);

  if (pharmacies.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <BuildingStorefrontIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Geen apotheken toegewezen
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Neem contact op met je beheerder om je toe te wijzen aan een
            apotheek.
          </p>
        </div>
      </div>
    );
  }

  if (pharmacies.length === 1 && !selectedPharmacy) {
    // Auto-select if only one pharmacy
    selectPharmacy(pharmacies[0]);
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-primary-600 rounded-full flex items-center justify-center mb-4">
            <BuildingStorefrontIcon className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Selecteer Apotheek
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Welkom terug, {user?.full_name}! Kies met welke apotheek je wilt
            werken.
          </p>
        </div>

        <div className="mt-8 space-y-4">
          {selectedPharmacy && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="relative w-full bg-white border border-gray-300 rounded-lg shadow-sm pl-3 pr-10 py-3 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <BuildingStorefrontIcon className="h-4 w-4 text-primary-600" />
                    </div>
                  </div>
                  <div className="ml-3 block truncate">
                    <div className="font-medium text-gray-900">
                      {selectedPharmacy.name}
                    </div>
                    <div className="text-sm text-gray-500 truncate">
                      {selectedPharmacy.address}
                    </div>
                  </div>
                </div>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </button>

              {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {pharmacies.map((pharmacy) => (
                    <button
                      key={pharmacy.id}
                      onClick={() => {
                        selectPharmacy(pharmacy);
                        setIsOpen(false);
                      }}
                      className={`w-full text-left relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-primary-50 ${
                        selectedPharmacy.id === pharmacy.id
                          ? "bg-primary-50"
                          : ""
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <BuildingStorefrontIcon className="h-4 w-4 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-3 block truncate">
                          <div className="font-medium text-gray-900">
                            {pharmacy.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {pharmacy.address}
                          </div>
                        </div>
                      </div>
                      {selectedPharmacy.id === pharmacy.id && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                          <CheckIcon
                            className="h-5 w-5 text-primary-600"
                            aria-hidden="true"
                          />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!selectedPharmacy && (
            <div className="space-y-3">
              {pharmacies.map((pharmacy) => (
                <button
                  key={pharmacy.id}
                  onClick={() => selectPharmacy(pharmacy)}
                  className="w-full bg-white border border-gray-300 rounded-lg p-4 text-left hover:bg-primary-50 hover:border-primary-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <BuildingStorefrontIcon className="h-5 w-5 text-primary-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {pharmacy.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {pharmacy.address}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {pharmacy.phone} • {pharmacy.email}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedPharmacy && (
            <div className="pt-4">
              <button
                onClick={() => {
                  // This will trigger the main app to show since we have a selected pharmacy
                }}
                className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Ga naar Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PharmacySelector;
